import { supabase } from '../lib/supabase';

export interface UserAction {
  id: string;
  user_id: string;
  organization_id: string;
  action_type: 'used_feature' | 'initiated_step' | 'completed_step' | 'completed_workflow';
  action_name: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface UserFeatureUsage {
  user_id: string;
  organization_id: string;
  feature_name: string;
  metadata: Record<string, any>;
  first_used: string;
  last_used: string;
  has_used: boolean;
}

export interface UserWorkflowProgress {
  user_id: string;
  organization_id: string;
  workflow_name: string;
  step_id: string | null;
  step_name: string | null;
  action_type: string;
  created_at: string;
  updated_at: string;
}

export class UserActionsService {
  /**
   * Track when a user uses a feature for the first time
   */
  static async trackFeatureUsage(featureName: string, metadata: Record<string, any> = {}): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_user_action', {
        p_action_type: 'used_feature',
        p_action_name: featureName,
        p_metadata: metadata
      });

      if (error) {
        console.error('Error tracking feature usage:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error tracking feature usage:', error);
      throw error;
    }
  }

  /**
   * Track when a user initiates a workflow step
   */
  static async trackStepInitiation(stepId: string, stepName: string, workflowName: string = 'default'): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_user_action', {
        p_action_type: 'initiated_step',
        p_action_name: workflowName,
        p_metadata: { step_id: stepId, step_name: stepName }
      });

      if (error) {
        console.error('Error tracking step initiation:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error tracking step initiation:', error);
      throw error;
    }
  }

  /**
   * Track when a user completes a workflow step
   */
  static async trackStepCompletion(stepId: string, stepName: string, workflowName: string = 'default'): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_user_action', {
        p_action_type: 'completed_step',
        p_action_name: workflowName,
        p_metadata: { step_id: stepId, step_name: stepName }
      });

      if (error) {
        console.error('Error tracking step completion:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error tracking step completion:', error);
      throw error;
    }
  }

  /**
   * Track when a user completes an entire workflow
   */
  static async trackWorkflowCompletion(workflowName: string, metadata: Record<string, any> = {}): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_user_action', {
        p_action_type: 'completed_workflow',
        p_action_name: workflowName,
        p_metadata: metadata
      });

      if (error) {
        console.error('Error tracking workflow completion:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error tracking workflow completion:', error);
      throw error;
    }
  }

  /**
   * Get all user actions
   */
  static async getUserActions(): Promise<UserAction[]> {
    try {
      const { data, error } = await supabase
        .from('user_actions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user actions:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching user actions:', error);
      throw error;
    }
  }

  /**
   * Get user feature usage
   */
  static async getUserFeatureUsage(): Promise<UserFeatureUsage[]> {
    try {
      const { data, error } = await supabase
        .from('user_feature_usage')
        .select('*')
        .order('first_used', { ascending: false });

      if (error) {
        console.error('Error fetching user feature usage:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching user feature usage:', error);
      throw error;
    }
  }

  /**
   * Get user workflow progress
   */
  static async getUserWorkflowProgress(): Promise<UserWorkflowProgress[]> {
    try {
      const { data, error } = await supabase
        .from('user_workflow_progress')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user workflow progress:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching user workflow progress:', error);
      throw error;
    }
  }

  /**
   * Check if a user has used a specific feature
   */
  static async hasUsedFeature(featureName: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_feature_usage')
        .select('has_used')
        .eq('feature_name', featureName)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned, user hasn't used the feature
          return false;
        }
        console.error('Error checking feature usage:', error);
        throw error;
      }

      return data?.has_used || false;
    } catch (error) {
      console.error('Error checking feature usage:', error);
      return false;
    }
  }

  /**
   * Check if a user has initiated a specific step
   */
  static async hasInitiatedStep(stepId: string, workflowName: string = 'default'): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_workflow_progress')
        .select('action_type')
        .eq('workflow_name', workflowName)
        .eq('step_id', stepId)
        .eq('action_type', 'initiated_step')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned, user hasn't initiated the step
          return false;
        }
        console.error('Error checking step initiation:', error);
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking step initiation:', error);
      return false;
    }
  }

  /**
   * Get workflow completion status
   */
  static async getWorkflowProgress(_workflowName: string = 'hiring-project'): Promise<{
    hasProjects: boolean;
    hasJobDescriptions: boolean;
    hasAnalyses: boolean;
    hasViewedMarketData: boolean;
  }> {
    try {
      const featureUsage = await this.getUserFeatureUsage();
      
      const usedFeatures = featureUsage.reduce((acc, usage) => {
        acc[usage.feature_name] = usage.has_used;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        hasProjects: usedFeatures['projects'] || false,
        hasJobDescriptions: usedFeatures['job-generator'] || false,
        hasAnalyses: usedFeatures['analysis'] || false,
        hasViewedMarketData: usedFeatures['market-intelligence'] || false
      };
    } catch (error) {
      console.error('Error getting workflow progress:', error);
      return {
        hasProjects: false,
        hasJobDescriptions: false,
        hasAnalyses: false,
        hasViewedMarketData: false
      };
    }
  }

  /**
   * Legacy localStorage compatibility - migrate localStorage data to Supabase
   */
  static async migrateLegacyData(): Promise<void> {
    try {
      // Check for legacy localStorage data and migrate it
      const legacyFeatures = [
        'job-generator',
        'analysis',
        'market-intelligence',
        'projects',
        'skills-assessment',
        'interview-management',
        'bias-detector'
      ];

      for (const feature of legacyFeatures) {
        const hasUsed = localStorage.getItem(`used_${feature}`) === 'true';
        if (hasUsed) {
          await this.trackFeatureUsage(feature, { migrated_from_localStorage: true });
        }
      }

      // Migrate initiated steps
      const allKeys = Object.keys(localStorage);
      const initiatedSteps = allKeys.filter(key => key.startsWith('initiated_'));
      
      for (const key of initiatedSteps) {
        const stepId = key.replace('initiated_', '');
        const hasInitiated = localStorage.getItem(key) === 'true';
        if (hasInitiated) {
          await this.trackStepInitiation(stepId, stepId, 'hiring-project');
        }
      }

      console.log('Legacy localStorage data migrated successfully');
    } catch (error) {
      console.error('Error migrating legacy data:', error);
    }
  }

  /**
   * Clear all user actions (for testing/development)
   */
  static async clearUserActions(): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_actions')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (error) {
        console.error('Error clearing user actions:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error clearing user actions:', error);
      throw error;
    }
  }
}

export default UserActionsService; 